import { useState } from 'react'

import dayjs from 'dayjs'

import {
  useDispatchMailApi,
  useMailQueueApi,
  useRetryMailApi
} from '@/common/api/modules/ugcMail'
import { MailQueueStatus } from '@/common/api/params/ugcMail'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

/**
 * 送信の状況。
 *
 * **「失敗」と「見送り」を分けて出す。** 宛先不明で送れなかったもの（失敗＝再送する）と、
 * 送らないと判断したもの（見送り＝再送しない）を1つの数字にまとめると、
 * 再送ボタンを押すべきかどうかが誰にも分からなくなる。
 */

const TABS: { key: MailQueueStatus; label: string; empty: string }[] = [
  { key: 'pending', label: '送信待ち', empty: '送信待ちはありません。' },
  { key: 'failed', label: '失敗', empty: '失敗した送信はありません。' },
  { key: 'skipped', label: '見送り', empty: '見送った送信はありません。' },
  { key: 'sent', label: '送信済み', empty: 'まだ送信したメールはありません。' }
]

const PER_PAGE = 50

const fmt = (v: string | null) => (v ? dayjs(v).format('YYYY/MM/DD HH:mm') : '—')

const MailQueuePanel = ({ siteId }: { siteId: number }) => {
  const [tab, setTab] = useState<MailQueueStatus>('pending')
  const [message, setMessage] = useState<string | null>(null)

  const { data, isLoading, mutate } = useMailQueueApi({
    site_id: siteId,
    status: tab,
    limit: PER_PAGE,
    offset: 0
  })

  const { trigger: dispatch, isMutating: dispatching } = useDispatchMailApi({
    onSuccess: (r) => {
      setMessage(
        r.reason
          ? `送信されませんでした: ${r.reason}`
          : `送信 ${r.sent}件 / 失敗 ${r.failed}件 / 見送り ${r.skipped}件`
      )
      mutate()
    },
    onError: (e) => setMessage(e instanceof Error ? e.message : '送信できませんでした')
  })

  const { trigger: retry, isMutating: retrying } = useRetryMailApi({
    onSuccess: (r) => {
      setMessage(`${r.restored}件を送信待ちに追加しました（元の失敗記録は残ります）`)
      mutate()
    },
    onError: (e) => setMessage(e instanceof Error ? e.message : '戻せませんでした')
  })

  const counts = data?.counts

  return (
    <Area>
      <Heading subText='自動送信は15分ごと（8〜21時・日本時間）に小分けで動きます。'>送信の状況</Heading>
      <AreaContent>
        <div className='flex flex-wrap items-center gap-2'>
          {TABS.map((t) => (
            <button
              key={t.key}
              type='button'
              onClick={() => {
                setTab(t.key)
                setMessage(null)
              }}
              className={
                tab === t.key
                  ? 'text-size-12 rounded-lg border border-border-brand bg-fill-brand px-3 py-1.5'
                  : 'text-size-12 rounded-lg border border-border-primary px-3 py-1.5'
              }
            >
              {t.label}
              {counts ? `（${counts[t.key]}）` : ''}
            </button>
          ))}

          <div className='ml-auto flex items-center gap-2'>
            {tab === 'failed' && (counts?.failed ?? 0) > 0 && (
              <Button
                color={ButtonColor.SECONDARY}
                loading={retrying}
                disabled={retrying}
                onClick={() => {
                  setMessage(null)
                  retry({ site_id: siteId })
                }}
              >
                失敗した分をもう一度送る
              </Button>
            )}
            <Button
              color={ButtonColor.PRIMARY}
              loading={dispatching}
              disabled={dispatching}
              onClick={() => {
                setMessage(null)
                dispatch({ site_id: siteId })
              }}
            >
              いま送信する
            </Button>
          </div>
        </div>

        {message && (
          <div className='mt-3'>
            <Text size={12} color={TextColor.SECONDARY}>
              {message}
            </Text>
          </div>
        )}

        <div className='mt-4'>
          {isLoading || !data ? (
            <Text color={TextColor.SECONDARY}>読み込み中...</Text>
          ) : data.items.length === 0 ? (
            <Text size={13} color={TextColor.SECONDARY}>
              {TABS.find((t) => t.key === tab)?.empty}
            </Text>
          ) : (
            <div className='overflow-x-auto'>
              <Table>
                <thead>
                  <TableRow head>
                    <TableHeadItem>
                      <Text size={12}>宛先</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>商品</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>種別</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>{tab === 'sent' ? '送信日時' : '送信予定'}</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>{tab === 'sent' ? '開封 / クリック / 投稿' : '状況'}</Text>
                    </TableHeadItem>
                  </TableRow>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableRowItem>
                        <Text size={12}>{item.customer_name || '—'}</Text>
                        <Text size={10} color={TextColor.TERTIARY}>
                          {item.sent_to || item.customer_email || '—'}
                        </Text>
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={12}>{item.product_name || '—'}</Text>
                        {item.order_id && (
                          <Text size={10} color={TextColor.TERTIARY}>
                            受注 {item.order_id}
                          </Text>
                        )}
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={12}>
                          {item.kind === 'followup1'
                            ? 'フォローアップ1回目'
                            : item.kind === 'followup2'
                              ? 'フォローアップ2回目'
                              : '初回'}
                          {item.attempt_no > 1 ? `（${item.attempt_no}回目の送信）` : ''}
                        </Text>
                        {/* 自動生成と、画面から個別に作ったものを区別する。
                            混ぜると開封率がどちらの数字なのか読めなくなる */}
                        <Text size={10} color={TextColor.TERTIARY}>
                          {item.source === 'manual' ? '個別に依頼' : '自動'}
                        </Text>
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={12}>{fmt(tab === 'sent' ? item.sent_at : item.scheduled_at)}</Text>
                      </TableRowItem>
                      <TableRowItem>
                        {tab === 'sent' ? (
                          <Text size={12}>
                            {item.opened_at ? '開封' : '—'} / {item.clicked_at ? 'クリック' : '—'} /{' '}
                            {item.submitted_at ? '投稿' : '—'}
                          </Text>
                        ) : (
                          <>
                            <Text size={12} color={TextColor.SECONDARY}>
                              {item.error_message || item.skip_reason || '送信待ち'}
                            </Text>
                          </>
                        )}
                      </TableRowItem>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
              {data.total > data.items.length && (
                <div className='mt-2'>
                  <Text size={10} color={TextColor.TERTIARY}>
                    {data.total}件のうち新しい{data.items.length}件を表示しています。
                  </Text>
                </div>
              )}
            </div>
          )}
        </div>
      </AreaContent>
    </Area>
  )
}

export default MailQueuePanel
